import { useCallback } from 'react';
import { useParams } from 'react-router';
import { isCheckInWorkflowPage } from 'routes';
import { useDelivery } from './useDelivery';
import { useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useShowCheckInExitWarningDialog } from 'hooks/useShowCheckInExitWarningDialog';

type Props = {
  showCheckInExitWarningDialog?: boolean;
};

export const useCheckForPartiallyCheckedInSets = ({ showCheckInExitWarningDialog = false }: Props = {}) => {
  const { deliveryId = '' } = useParams();
  const { data: delivery } = useDelivery(deliveryId);

  const [checkInTraysBySet] = useCheckInTraysBySet();

  const [_, setShowCheckInExitWarningDialog] = useShowCheckInExitWarningDialog();

  // onConfirmExit is only used when showCheckInExitWarningDialog is true; it's what runs if the
  // user chooses to exit anyway (e.g. navigate to the clicked route, or log out)
  const checkForPartiallyCheckedInSets = useCallback(
    (onConfirmExit?: VoidFunction): boolean => {
      if (
        isCheckInWorkflowPage(location.pathname) &&
        (delivery?.status === 'RECEIVED' || delivery?.status === 'PARTIALLY_CHECKED_IN')
      ) {
        const hasPartiallyCheckedInSets = delivery?.sets.some(
          set =>
            checkInTraysBySet[set.kitInstanceId] &&
            (!checkInTraysBySet[set.kitInstanceId].trays ||
              (checkInTraysBySet[set.kitInstanceId].trays &&
                Object.values(checkInTraysBySet[set.kitInstanceId].trays).some(tray => !tray.completed))),
        );

        if (hasPartiallyCheckedInSets) {
          // in some cases, like the beforeunload, we use the browser dialog to show the warning
          if (showCheckInExitWarningDialog) {
            setShowCheckInExitWarningDialog({ show: true, onConfirm: onConfirmExit });
          }

          return true;
        }
      }

      return false;
    },
    [
      delivery?.status,
      delivery?.sets,
      checkInTraysBySet,
      showCheckInExitWarningDialog,
      setShowCheckInExitWarningDialog,
    ],
  );

  return checkForPartiallyCheckedInSets;
};
